import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva('inline-block text-center truncate shrink items-center border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 py-0.5 cursor-default max-w-full', {
  variants: {
    variant: {
      default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
      'primary-light': 'border-transparent bg-primary-light text-white hover:bg-primary-light/80',
      secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
      third: 'border-transparent bg-third text-third-foreground hover:bg-third/80',
      destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
      outline: 'text-foreground',
      success: 'border-transparent bg-success text-white hover:bg-success/80',
      'success-light': 'border-transparent bg-success-light text-white hover:bg-success-light/80',
      warning: 'border-transparent bg-warning text-white hover:bg-warning/80',
      danger: 'border-transparent bg-danger text-white hover:bg-danger/80',
      info: 'border-transparent bg-info text-white',
      more: 'border-transparent bg-primary-lightest text-primary cursor-help shrink-0',
      bright: 'border-transparent bg-bright text-neutral',
      attention: 'border-transparent bg-attention text-white',
      neutral: 'border-transparent bg-neutral text-white',
      'neutral-lightest': 'border-transparent bg-neutral-lightest text-neutral-lightest-foreground hover:bg-neutral-lightest/80',
      // Variantes do tema playful. Sufixo "-soft" = fundo claro + texto colorido
      // (diferente do "-light" acima, que é fundo claro + texto branco).
      amber: 'border-transparent bg-mission text-white hover:bg-mission/80',
      'amber-soft': 'border-transparent bg-mission-light text-mission hover:bg-mission-light/80',
      rose: 'border-transparent bg-merit text-white hover:bg-merit/80',
      'rose-soft': 'border-transparent bg-merit-light text-merit hover:bg-merit-light/80',
      red: 'border-transparent bg-[#FF5630] text-white hover:bg-[#FF5630]/80',
      'info-soft': 'border-transparent bg-info-light text-info-dark hover:bg-info-light/80',
      'success-soft': 'border-transparent bg-success-lightest text-success hover:bg-success-lightest/80',
      'danger-soft': 'border-transparent bg-danger-lighter text-danger hover:bg-danger-lighter/80',
      muted: 'border-transparent bg-neutral-100 text-neutral-500'
    },
    size: {
      default: 'text-sm px-4 leading-[20px]',
      xs: 'text-xs leading-[16px] py-0 px-2 font-light',
      sm: 'text-xs px-2'
    },
    square: {
      true: 'rounded-sm py-0.5 px-2',
      false: 'rounded-full'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    square: false
  }
});

function Badge({ className, variant, size, square, ...props }) {
  return (
    <div
      className={cn('c-badge', badgeVariants({ variant, size, square }), className)}
      {...props}
    />
  );
}

const BadgeGroup = React.forwardRef(({ className, oneLine = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex max-w-full flex-wrap items-center gap-1 leading-none', { 'flex-nowrap': oneLine }, className)}
    {...props}
  />
));
BadgeGroup.displayName = 'BadgeGroup';

const BadgeTruncate = React.forwardRef(({ items, initialCount = 3, oneLine = false, variant = 'bright', size = 'xs', className, ...props }, ref) => {
  if (!items || items.length === 0) {
    return null;
  }

  // Pega os itens a serem exibidos inicialmente
  const displayedItems = items.slice(0, initialCount);
  // Calcula quantos itens restam
  const remainingItemsCount = items.length - initialCount;
  // Verifica se há mais itens para mostrar
  const hasMore = remainingItemsCount > 0;

  return (
    <BadgeGroup
      ref={ref}
      oneLine={oneLine}
      className={cn('c-badge-truncate', className)}
      {...props}
    >
      {/* Renderiza os badges visíveis */}
      {displayedItems.map((item, index) => (
        <Badge
          variant={variant}
          size={size}
          key={index}
          title={item?.name}
        >
          {item?.name}
        </Badge>
      ))}

      {/* Renderiza o badge "+X" se houver mais itens */}
      {hasMore && (
        <Badge
          variant="more"
          size={size}
          // Adiciona um título com os nomes dos badges restantes para acessibilidade
          title={items
            .slice(initialCount)
            .map(t => ` ${t.name}`)
            .join(', ')
            .trim()}
        >
          +{remainingItemsCount}
        </Badge>
      )}
    </BadgeGroup>
  );
});
BadgeTruncate.displayName = 'BadgeTruncate';

export { Badge, badgeVariants, BadgeGroup, BadgeTruncate };
